import React, { useState, useEffect } from 'react';
import { Download, Edit, Save, X } from 'lucide-react';
import apiService from '../config';

const Punavarshan = () => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState('');
  const [projectName, setProjectName] = useState('');
  const [locationInfo, setLocationInfo] = useState({
    district: 'बुलढाणा',
    taluka: '',
    village: ''
  });

  // ===================== EDIT MODAL STATE =====================
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedOwnerIndex, setSelectedOwnerIndex] = useState(null);
  const [formData, setFormData] = useState({});

  const [projectId, setProjectId] = useState(null);
  const [proposalId, setProposalId] = useState(null);
  const [fullName, setFullName] = useState('');
  const [designation , setDesignation] =useState('');

  const ROLES = {
    COLLECTOR: 1,
    LAO: 3,
    SAMANVAY: 6,
    PROJECT_INCHARGE: 18,
    DATA_ENTRY: 14
  };

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);

  // ===================== GET PROJECT / PROPOSAL =====================
  useEffect(() => {
    const getProjectId = () => {
      const currentProjectData = localStorage.getItem('currentProjectData');
      if (currentProjectData) {
        const p = JSON.parse(currentProjectData);
        return p.project_id || p.id;
      }

      const selectedProject = localStorage.getItem('selectedProject');
      if (selectedProject) {
        const p = JSON.parse(selectedProject);
        return p.project_id || p.id;
      }

      if (userData?.data?.project_id || userData?.project_id) {
        return userData.data.project_id || userData.project_id;
      }
      return null;
    };

    const getProposalId = () => {
      return (
        userData?.data?.default_proposal ||
        userData?.data?.proposal_id ||
        userData?.default_proposal ||
        userData?.proposal_id ||
        null
      );
    };

    setProjectId(getProjectId());
    setProposalId(getProposalId());
  }, []);

  // ===================== FETCH DATA =====================
  useEffect(() => {
    const fetchPunarvarshanDetails = async () => {
      if (!projectId || !proposalId) return;

      try {
        setLoading(true);

        // GET_PUNARVARSHAN: '/getLandPunarvarshan'
        const response = await apiService.getPunarvarshanDetails(projectId, proposalId);

        // API returns a single object (based on backend response)
        const rawData = response?.data || response;

        if (rawData && !Array.isArray(rawData)) {
          // Single record — wrap in array so the existing map() rendering works
          const mapped = mapRecord(rawData, 0);
          setOwners([mapped]);

          setLocationInfo({
            district: 'बुलढाणा',
            taluka: '',
            village: ''
          });
        } else if (Array.isArray(rawData) && rawData.length > 0) {
          setLocationInfo({
            district: rawData[0].dist_name || 'बुलढाणा',
            taluka: rawData[0].taluka_name || '',
            village: rawData[0].village_name || ''
          });
          setOwners(rawData.map(mapRecord));
        }
      } catch (err) {
        console.error('Error fetching Punarvarshan details:', err);
        setError('पुनर्वसन सुरू झालेले नाही');
      } finally {
        setLoading(false);
      }
    };

    fetchPunarvarshanDetails();
  }, [projectId, proposalId]);

  // Maps a raw API record to the shape used by the UI
  const mapRecord = (item, index) => {
    // owner_id comes directly from the backend as "owner_id" (e.g. 7029)
    // register_no is a separate field (e.g. 10) — NOT the owner_id
    const resolvedOwnerId =
      item.owner_id ??
      item.land_owner_id ??
      item.rehab_id ??
      null;

    return {
      id: resolvedOwnerId || index + 1,
      owner_id: resolvedOwnerId,
      applicantName: item.Applicant1 || item.family_project_affected_certificate_person_name || item.owner_name || '',
      applicant2: item.Applicant2 || 'उपजिल्हाधिकारी भूसंपादन (मध्यम प्रकल्प) बुलढाणा यांचे पत्र दि.',
      applicantRelation: item.family_project_affected_relation || 'स्वयं',
      village: item.village_name || '',
      taluka: item.taluka_name || '',
      surveyNumber: item.survey_no || item.house_no || '-',
      area: item.owner_aqr_area ? `${item.owner_aqr_area} चौरस मीटर` : '---',
      registration: item.register_no != null ? String(item.register_no) : (item.acquisition_proposal_no || '-'),
      pageno: item.page_no != null ? String(item.page_no) : '',
      notification_no: item.notification_no || 'जा.क्र. कावि/कक्ष/२१/ पुनर्वसन/ /२०२५',
      office_name: item.office_name || 'जिल्हाधिकारी कार्यालय, बुलढाणा',
      date: item.date || '',
      default_content:
        item.default_content ||
        `१. दाखल करण्यात येतो की, श्री/श्रीमती ${item.owner_name || '________'} रा. ${item.village_name || '______'} ता. ${item.taluka_name || '______'} जि. बुलढाणा यांचे मालकीचे गट नं./सर्व्हे नं. मधील 500 चौरस मीटर क्षेत्रफळाची जमीन ${projectName} प्रकल्पाचे बुडीत क्षेत्राच्या प्रयोजनासाठी संपादीत केल्यामुळे ते प्रकल्पग्रस्त आहेत.

२. दाखला देण्यात येतो की, ${item.family_project_affected_certificate_person_name || item.owner_name} ही उपरोक्त प्रकल्पग्रस्त व्यक्तीच्या कुटुंबातील/अवलंबून असणारी व्यक्ती असून त्यांचे प्रकल्पग्रस्त व्यक्तीशी नाते  ${item.family_project_affected_relation || 'स्वयं'} आहे.

३.  शासनाच्या पुनर्वसन योजने अंतर्गत सदर प्रकल्पग्रस्त व्यक्तीला किंवा तिच्या कुटुंबातील व तिच्यावर अवलंबून असणाऱ्या उपरोक्त एकाच व्यक्तीस नोकरी मिळण्याच्या संदर्भात सदर दाखला परिणामकारक राहील आणि वरीलप्रमाणे संबंधित व्यक्तीस नोकरी मिळाल्यावर दाखल्याची परिणामकारकता रद्द होईल.

४. वरील दाखल्याच्या आधारे प्रकल्पग्रस्त व्यक्ती किंवा तिच्यावर अवलंबून असणाऱ्या एका व्यक्तीस नोकरी देण्याची कार्यवाही करणाऱ्या कार्यालयाने नोकरी दिल्याचा तपशील उपजिल्हाधिकारी पुनर्वसन, बुलढाणा यांचेकडे तात्काळ कळविणे आवश्यक आहे. तसेच हे मूळ प्रमाणपत्र त्यांच्या कार्यालयाच्या दप्तरी दाखल करून घेण्यात यावे. तसेच अर्जदाराने सादर केलेली कागदपत्रे दिशाभूल करणारी किंवा चुकीची आढळल्यास सदर प्रमाणपत्र रद्द करण्यात येईल.`,
      remarks: item.remarks || ''
    };
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    setFullName(userData.full_name || '');
    setDesignation(userData.designation || '');
  }, []);

  useEffect(() => {
    const today = new Date();
    setCurrentDate(today.toLocaleDateString('en-GB')); // dd/mm/yyyy

    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
    setProjectName(selectedProject.project_name || selectedProject.name || 'प्रकल्प');
  }, []);

  // ===================== EDIT HANDLERS =====================

  const handleEditClick = (ownerIndex) => {
    const owner = owners[ownerIndex];
    setSelectedOwnerIndex(ownerIndex);
    setFormData({
      owner_id: owner.owner_id,
      applicantName: owner.applicantName,
      applicant2: owner.applicant2,
      applicantRelation: owner.applicantRelation,
      registration: owner.registration,
      pageno: owner.pageno,
      notification_no: owner.notification_no,
      office_name: owner.office_name,
      date: owner.date,
      default_content: owner.default_content
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!projectId || !proposalId) {
      alert('Project / Proposal ID missing');
      return;
    }

    if (!formData.owner_id) {
      alert('owner_id is missing — cannot save. Please refresh and try again.');
      return;
    }

    try {
      setSaving(true);

      // Update local owners list immediately
      const updatedOwners = owners.map((item, idx) => {
        if (idx !== selectedOwnerIndex) return item;
        return {
          ...item,
          applicantName: formData.applicantName,
          applicant2: formData.applicant2,
          applicantRelation: formData.applicantRelation,
          registration: formData.registration,
          pageno: formData.pageno,
          notification_no: formData.notification_no,
          office_name: formData.office_name,
          date: formData.date,
          default_content: formData.default_content
        };
      });
      setOwners(updatedOwners);

      // POST — api: postAddUpdateLandPunarvarshan
      const payload = {
        project_id: projectId,
        proposal_id: proposalId,
        owner_id: formData.owner_id,
        register_no: formData.registration,
        page_no: formData.pageno,
        Applicant1: formData.applicantName,
        Applicant2: formData.applicant2,
        notification_no: formData.notification_no,
        office_name: formData.office_name,
        date: formData.date,
        default_content: formData.default_content
      };

      await apiService.postAddUpdateLandPunarvarshan(payload);

      setOpen(false);
      alert('✓ पुनर्वसन यशस्वीपणे जतन झाले');
    } catch (err) {
      console.error('Save failed:', err);
      alert('❌ जतन करताना त्रुटी आली');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  if (loading) return <div className="text-center p-10">लोड होत आहे...</div>;
  if (error) return <div className="text-red-600 text-center p-10">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">

      {/* ===================== Action Buttons ===================== */}
      <div className="max-w-5xl mx-auto mb-6 flex justify-start gap-4">
        <button
          onClick={handleDownloadPDF}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 shadow"
        >
          <Download size={18} />प्रिंट
        </button>
      </div>

      {/* ===================== Print Area ===================== */}
      <div id="pdf-content" className="max-w-4xl mx-auto bg-white">
        {owners.map((owner, ownerIndex) => {
          // Resolve display date: prefer owner.date from API, fallback to currentDate
          const displayDate = owner.date
            ? new Date(owner.date).toLocaleDateString('en-GB')
            : currentDate;

          return (
            <div
              key={owner.id}
              className="page bg-white p-10"
              style={{
                fontFamily: "'Noto Sans Devanagari', 'Mangal', Arial, serif",
                fontSize: '13.5px',
                lineHeight: '1.45'
              }}
            >
              {/* ---- Owner-wise Edit Button (hidden on print) ---- */}
              <div className="flex justify-end mb-2 print:hidden">
                <button
                  onClick={() => handleEditClick(ownerIndex)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-lg flex items-center gap-2 shadow text-sm"
                >
                  <Edit size={16} /> संपादित करा
                </button>
              </div>

              {/* Header / Registration Info */}
              <div className="flex justify-end mb-6">
                <div className="border border-black text-sm">
                  <div className="px-6 py-2 border-b border-black">
                    रजि क्र.{' '}
                    <span className="border-b border-dotted px-2 min-w-[100px] inline-block">
                      {owner.registration}
                    </span>
                  </div>
                  <div className="px-6 py-2">
                    पान क्र.{' '}
                    <span className="border-b border-dotted px-2 min-w-[60px] inline-block">
                      {owner.pageno || '......'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Government Orders */}
              <div className="text-sm space-y-1 mb-6">
                <div>१. महाराष्ट्र शासन महसूल व वनमंत्रालय, शासन निर्णय क्रमांक उपायोजना क्र. ०४ वा /२०२१/प्र.क्र.३११/२०२१.</div>
                <div>२. महाराष्ट्र शासन महसूल व वनमंत्रालय, शासन निर्णय क्रमांक आरपीएसएल/१४४७ दि.१२/०३/२० दि.२०/०४/२०२१.</div>
                <div>३. महाराष्ट्र शासन महसूल व वनमंत्रालय, शासन निर्णय क्रमांक आरपीएसएल/०३०४ दि.१२/०८/२०२०.</div>
                <div>४. महाराष्ट्र शासन महसूल व वनमंत्रालय, शासन निर्णय क्रमांक आरपीएसएल/०३०४ दि.२१/०४/२०२०.</div>
                <div>५. महाराष्ट्र शासन महसूल व वनमंत्रालय, शासन निर्णय क्रमांक आरपीएसएल/०३०४ दि.२१/०४/२०२०.</div>
              </div>

              <div className="border-t border-dashed border-black my-6"></div>

              {/* Application & Letter Info */}
              <div className="text-sm mb-8">
                <div>
                  अर्जदार :-  १.{' '}
                  <span className="border-b border-dotted px-1">{owner.applicantName}</span>
                </div>
                <div className="ml-14 mt-1">
                  २.{' '}
                  <span>{owner.applicant2 || 'उपजिल्हाधिकारी भूसंपादन (मध्यम प्रकल्प) बुलढाणा यांचे पत्र दि. 15/01/2026'}</span>
                </div>

                <div className="mt-4 text-right">
                  <div>{owner.notification_no}</div>
                  <div>{owner.office_name}</div>
                  <div>दिनांक :- {displayDate}</div>
                </div>
              </div>

              {/* Certificate Title */}
              <div className="text-center font-bold text-lg underline mb-6">
                प्रमाणपत्र
              </div>

              {/* Certificate Content — use default_content from API if available */}
              {owner.default_content ? (
                <div className="space-y-5 text-justify whitespace-pre-line">
                  {owner.default_content}
                </div>
              ) : (
                <div className="space-y-5 text-justify">
                  <div>
                    <strong>१.</strong> दाखल करण्यात येतो की, श्री/श्रीमती{' '}
                    <span className="border-b border-dotted px-1">{owner.applicantName}</span>{' '}
                    रा. {owner.village || locationInfo.village || '......'} ता. {owner.taluka || locationInfo.taluka || '......'} जि.बुलढाणा यांचे मालकीचे गट नं./सर्व्हे नं.{' '}
                    मधील {owner.area || '............'} क्षेत्रफळाची जमीन{' '}
                    <strong>{projectName}</strong> प्रकल्पाचे बुडीत क्षेत्राच्या प्रयोजनासाठी संपादीत केल्यामुळे ते प्रकल्पग्रस्त आहेत.
                  </div>

                  <div>
                    <strong>२.</strong> दाखला देण्यात येतो की{' '}
                    <span className="border-b border-dotted px-1">{owner.applicantName}</span>{' '}
                    ही उपरोक्त प्रकल्पग्रस्त व्यक्तीच्या कुटुंबातील/अवलंबून असणारी व्यक्ती असून त्यांचे प्रकल्पग्रस्त व्यक्तीशी नाते {owner.applicantRelation} आहे.
                  </div>

                  <div>
                    <strong>३.</strong> शासनाच्या पुनर्वसन योजने अंतर्गत सदर प्रकल्पग्रस्त व्यक्तीला किंवा तिच्या कुटुंबातील आणि तिच्यावर अवलंबुन असणाऱ्या
                    उपरोक्त एकाच व्यक्तीला नोकरी मिळण्याचे संदर्भात सदर दाखला परिणामकारक राहिल आणि वरील प्रमाणे संबंधित व्यक्तीला
                    नोकरी मिळाल्यावर दाखल्याची परिणामकारकता रद्द होईल.
                  </div>

                  <div>
                    <strong>४.</strong> वरील दाखल्याचे आधारे प्रकल्पग्रस्त व्यक्ती किंवा तिच्यावर अवलंबुन असणा-या एका व्यक्तिला नोकरी देण्याची कार्यवाही
                    करण्या-या कार्यालयाने नोकरी दिल्याचा तपशील उपजिल्हाधिकारी पुनर्वसन बुलढाणा यांचेकडे तात्काळ कळविणे आवश्यक
                    आहे. तसेच हे मुळ प्रमाणपत्र त्यांचे कार्यालयाचे दप्तरी दाखल करुन घेण्यात यावे. तसेच अर्जदाराने सादर केलेली कागदपत्र
                    दिशाभुल करणारी चुकीचे सादर केल्याचे निर्देशनास आल्यास सदर प्रमाणपत्र रद्द करण्यात करण्यात येईल.
                  </div>
                </div>
              )}

              {/* Signature */}
              <div className="mt-20 text-right">
                <div className="font-bold">{fullName}</div>
                <div>{designation}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===================== EDIT MODAL ===================== */}
      {open && selectedOwnerIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">

            {/* Modal Header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {owners[selectedOwnerIndex]?.applicantName
                  ? `${owners[selectedOwnerIndex].applicantName} - संपादित करा`
                  : 'पुनर्वसन प्रमाणपत्र संपादित करा'}
              </h2>
              <button onClick={() => setOpen(false)}>
                <X size={22} />
              </button>
            </div>

            <div className="space-y-4">

              {/* Applicant 1 */}
              <p><strong>अर्जदार १ </strong></p>
              <input
                value={formData.applicantName || ''}
                onChange={(e) => setFormData({ ...formData, applicantName: e.target.value })}
                placeholder="अर्जदाराचे नाव व पत्ता"
                className="w-full border p-2 rounded"
              />

              {/* Applicant 2 */}
              <p><strong>अर्जदार २ </strong></p>
              <input
                value={formData.applicant2 || ''}
                onChange={(e) => setFormData({ ...formData, applicant2: e.target.value })}
                placeholder="उपजिल्हाधिकारी / पत्र तपशील"
                className="w-full border p-2 rounded"
              />

              {/* Register No */}
              <p><strong>रजि क्र. </strong></p>
              <input
                value={formData.registration}
                onChange={(e) => setFormData({ ...formData, registration: e.target.value })}
                placeholder="रजिस्टर नंबर"
                className="w-full border p-2 rounded"
              />

              {/* Page No */}
              <p><strong>पान क्र. </strong></p>
              <input
                value={formData.pageno || ''}
                onChange={(e) => setFormData({ ...formData, pageno: e.target.value })}
                placeholder="पान नंबर"
                className="w-full border p-2 rounded"
              />

              {/* Notification No */}
              <p><strong>जा.क्र. </strong></p>
              <input
                value={formData.notification_no || ''}
                onChange={(e) => setFormData({ ...formData, notification_no: e.target.value })}
                placeholder="जा.क्र."
                className="w-full border p-2 rounded"
              />

              {/* Office Name */}
              <p><strong>कार्यालय </strong></p>
              <input
                value={formData.office_name || ''}
                onChange={(e) => setFormData({ ...formData, office_name: e.target.value })}
                placeholder="कार्यालयाचे नाव"
                className="w-full border p-2 rounded"
              />

              {/* Date */}
              <p><strong>दिनांक </strong></p>
              <input
                type="date"
                value={formData.date ? formData.date.split('T')[0] : ''}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full border p-2 rounded"
              />

              {/* Default Content */}
              <p><strong>प्रमाणपत्र मजकूर </strong></p>
              <textarea
                rows={8}
                value={formData.default_content || ''}
                onChange={(e) => setFormData({ ...formData, default_content: e.target.value })}
                className="w-full border p-2 rounded"
                placeholder="प्रमाणपत्राचा मजकूर..."
              />
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded"
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded disabled:opacity-60"
              >
                {saving ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} /> Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #pdf-content,
          #pdf-content * {
            visibility: visible;
          }
          #pdf-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .page {
            page-break-after: always;
          }
          .page:last-child {
            page-break-after: avoid;
          }
          @page {
            size: A4;
            margin: 0;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Punavarshan;